import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Divider,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import axios from 'axios';

// Image files under frontend/public/ that users can pick as avatar (path from site root)
const PUBLIC_AVATAR_IMAGES = [
  '/hacker.png',
  '/favicon_watchtower/WT_TransparentBG.png',
  '/Ubiquity/orange.png',
  '/Ubiquity/play.png',
  '/Ubiquity/CGU.avif',
  '/Ubiquity/U7 Pro.avif',
  '/Ubiquity/USW Flex 2.5G.avif',
  '/apple-touch-icon.png',
  '/dashboard-icons.png',
  '/2fauth-light.png',
  '/adguard-home.png',
  '/cockpit-light.png',
  '/ddns-updater.png',
  '/disk.png',
  '/docker-moby.png',
  '/dockge.png',
  '/fastapi.png',
  '/filebrowser.png',
  '/glances-light.png',
  '/gramps-web.png',
  '/hbo-light.png',
  '/hdd.png',
  '/home-assistant-alt.png',
  '/immich.png',
  '/intel.png',
  '/jellyfin.png',
  '/jellystat.png',
  '/linux.png',
  '/nvme.png',
  '/oralb.png',
  '/parquet.png',
  '/qbittorrent.png',
  '/RAM.png',
  '/raspberry-pi.png',
  '/reactjs.png',
  '/server.png',
  '/sqlitebrowser.png',
  '/ssd.png',
  '/task_scheduler.png',
  '/truenas-scale.png',
  '/truenas_logo.png',
  '/ubuntu-linux.png',
  '/unifi.png',
  '/unifi-controller.png',
  '/victron-energy.png',
  '/watchtower.png',
  '/webdav.png',
  '/wled.png',
  '/windows-10.png',
  '/windows-11.png',
  '/wordpress.png',
  '/xiaomi-global.png',
  '/zigbee.png',
];

const EditProfileDialog = ({
  open,
  onClose,
  apiBaseUrl,
  token,
  userInfo,
  language,
  sidebarConfig,
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open && userInfo) {
      setEmail(userInfo.email ?? '');
      setAvatarUrl(userInfo.avatar_url ?? '');
      setPassword('');
      setPasswordConfirm('');
      setErrorMessage('');
    }
  }, [open, userInfo]);

  const handleSave = async () => {
    if (password && password !== passwordConfirm) {
      setErrorMessage('Passwords do not match');
      return;
    }
    if (password && password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }
    setErrorMessage('');
    setSaving(true);
    try {
      const username = userInfo?.username;
      if (!username) {
        setErrorMessage('User not found');
        setSaving(false);
        return;
      }
      if (password || email !== (userInfo?.email ?? '')) {
        const res = await axios.put(
          `${apiBaseUrl}/api/users/${encodeURIComponent(username)}`,
          {
            password: password || undefined,
            email: email !== undefined && email !== null ? email : undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.data.success && res.data.message) {
          setErrorMessage(res.data.message);
          setSaving(false);
          return;
        }
      }
      const prefPayload = {
        language: language || 'en',
        sidebar_config: sidebarConfig ?? null,
        avatar_url: avatarUrl && avatarUrl.trim() ? avatarUrl.trim() : null,
      };
      await axios.post(
        `${apiBaseUrl}/api/user/preferences`,
        prefPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSave?.();
      onClose();
    } catch (err) {
      setErrorMessage(err.response?.data?.message || err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: '#fff',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(30, 30, 40, 0.95), rgba(40, 40, 50, 0.95))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      <DialogTitle sx={{ color: '#00d4ff', fontWeight: 600 }}>
        Edit profile — {userInfo?.username}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {errorMessage && (
          <Typography sx={{ color: theme.palette.error.main, mb: 2, fontSize: '0.875rem' }}>
            {errorMessage}
          </Typography>
        )}

        {/* Avatar */}
        <Typography variant="subtitle2" sx={{ color: '#00d4ff', fontWeight: 600, mb: 1 }}>
          Avatar
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
          Choose an image from the app folder or enter a custom path (e.g. /my-image.png).
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          {PUBLIC_AVATAR_IMAGES.map((path) => (
            <Box
              key={path}
              onClick={() => setAvatarUrl(path)}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                overflow: 'hidden',
                border: avatarUrl === path ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': { borderColor: 'rgba(0, 212, 255, 0.5)' },
              }}
            >
              <img
                src={path}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          ))}
        </Box>
        <TextField
          fullWidth
          size="small"
          label="Custom avatar path"
          value={avatarUrl}
          onChange={(e) => setAvatarUrl(e.target.value)}
          placeholder="/path/to/image.png"
          helperText="Path from site root; image must be in the app public folder."
          sx={{ ...textFieldSx, mb: 2 }}
        />

        <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <TextField
          fullWidth
          label="Email for 2FA"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          placeholder="e.g. user@example.com"
          helperText="Separate multiple emails with commas"
          sx={textFieldSx}
        />
        <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 1, display: 'block' }}>
          Leave password fields empty to keep current password.
        </Typography>
        <TextField
          fullWidth
          label="New Password"
          name="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          placeholder="Enter new password"
          sx={textFieldSx}
        />
        <TextField
          fullWidth
          label="Confirm New Password"
          name="password_confirm"
          type="password"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          margin="normal"
          placeholder="Re-enter new password"
          sx={textFieldSx}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            backgroundColor: (t) => t.palette.secondary.main,
            color: (t) => t.palette.secondary.contrastText,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: (t) => t.palette.secondary.main,
              filter: 'brightness(1.1)',
            },
          }}
        >
          {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <CheckIcon sx={{ mr: 1 }} />}
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProfileDialog;
